import type {
	FieldPath,
	FieldValues,
	UseControllerProps,
} from 'react-hook-form'

import React from 'react'
import { CircleHelpIcon } from 'lucide-react'
import { useFormContext } from 'react-hook-form'

import {
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/ui/form'
import { Textarea } from '@/shared/components/ui/textarea'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'

export default function RHFTextArea<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	name,
	label,
	hint,
	description,
	defaultValue,
	shouldUnregister,
	...other
}: UseControllerProps<TFieldValues, TName> &
	React.ComponentProps<typeof Textarea> & {
		label: string
		hint?: string
		description?: string
		required?: boolean
	}) {
	const { control } = useFormContext()

	return (
		<FormField
			name={name}
			defaultValue={defaultValue}
			shouldUnregister={shouldUnregister}
			control={control}
			render={({ field, fieldState: { error } }) => (
				<FormItem>
					<FormLabel>
						{label}
						{hint && (
							<Tooltip>
								<TooltipTrigger asChild>
									<CircleHelpIcon className='size-3 text-muted-foreground' />
								</TooltipTrigger>
								<TooltipContent side='top'>{hint}</TooltipContent>
							</Tooltip>
						)}
					</FormLabel>
					<FormControl>
						<Textarea {...other} {...field} invalid={!!error} />
					</FormControl>
					{!!description && <FormDescription>{description}</FormDescription>}
					<FormMessage />
				</FormItem>
			)}
		/>
	)
}
